import { useTranslation } from 'react-i18next';
import { withStyles, Box, BodyText, Card } from 'vopay';
import type { Domain } from 'vopay';
import { Title, Button, Table, CopyButton, TransactionStatus } from 'UI';
import styles from './SendingDomainCard.module.scss';

const cn = withStyles(styles);

type SendingDomainCardProps = {
  domain: Domain;
  busy: boolean;
  onVerify: () => void;
  onRemove: () => void;
};

type CnameRecordRow = {
  id: 'DKIM' | 'ReturnPath';
  Hostname: string;
  Target: string;
  Text: string;
  Verified: boolean;
};

export default function SendingDomainCard({ domain, busy, onVerify, onRemove }: SendingDomainCardProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Settings.EmailDomain' });
  const isVerified = domain.Status === 'verified';

  const rows: CnameRecordRow[] = [
    { id: 'DKIM', ...domain.CnameRecords.DKIM },
    { id: 'ReturnPath', ...domain.CnameRecords.ReturnPath },
  ];

  return (
    <Card className={cn('card')}>
      <Box flx="row" ai="center" gapSize="12px">
        <Title level="5">{domain.Domain}</Title>
        <TransactionStatus
          status={isVerified ? 'positive' : 'neutral'}
          label={
            isVerified
              ? t('Badge.verified', { defaultValue: 'Verified' })
              : t('Badge.unverified', { defaultValue: 'Unverified' })
          }
        />
      </Box>

      <div className={cn('divider')} />

      <Box flx="col" gapSize="4px">
        {isVerified ? (
          <>
            <BodyText>
              {t('verifiedInstructions', {
                defaultValue: 'Emails sent on your behalf are now authenticated with {{domain}}.',
                domain: domain.Domain,
              })}
            </BodyText>

            <BodyText size="sm" className={cn('instructionsHelper')}>
              {t('verifiedInstructionsHelper', {
                defaultValue:
                  'Keep these CNAME records in place at your DNS provider. Removing them will stop authentication.',
              })}
            </BodyText>
          </>
        ) : (
          <>
            <BodyText>
              {domain.Registrar
                ? t('instructionsWithRegistrar', {
                    defaultValue:
                      'Add these two CNAME records at your DNS provider ({{registrar}}), then click Verify.',
                    registrar: domain.Registrar,
                  })
                : t('instructionsWithoutRegistrar', {
                    defaultValue: 'Add these two CNAME records at your DNS provider, then click Verify.',
                  })}
            </BodyText>

            <BodyText size="sm" className={cn('instructionsHelper')}>
              {t('instructionsHelper', {
                defaultValue: 'DNS changes usually take a few minutes but can take up to an hour.',
              })}
            </BodyText>
          </>
        )}
      </Box>

      <Table
        id="cnameRecords"
        zebra
        columns={[
          {
            id: 'type',
            header: t('Table.type', { defaultValue: 'Type' }),
            align: 'left',
            template: () => t('Table.cname', { defaultValue: 'CNAME' }),
          },
          {
            id: 'hostName',
            header: t('Table.hostName', { defaultValue: 'Host Name' }),
            align: 'left',
            template: ({ data }) => (
              <Box flx="col" gapSize="4px">
                <Box flx="row" ai="center" gapSize="8px">
                  <BodyText size="sm">{data.Hostname}</BodyText>

                  <CopyButton content={data.Hostname} size="20px" />
                </Box>

                <span className={cn('fullHostname')}>{data.Text}</span>
              </Box>
            ),
          },
          {
            id: 'value',
            header: t('Table.value', { defaultValue: 'Value' }),
            align: 'left',
            template: ({ data }) => (
              <Box flx="row" ai="center" gapSize="8px">
                <BodyText size="sm">{data.Target}</BodyText>

                <CopyButton content={data.Target} size="20px" />
              </Box>
            ),
          },
          {
            id: 'status',
            header: t('Table.status', { defaultValue: 'Status' }),
            align: 'left',
            template: ({ data }) => (
              <TransactionStatus
                status={data.Verified ? 'positive' : 'negative'}
                label={
                  data.Verified
                    ? t('Table.found', { defaultValue: 'Found' })
                    : t('Table.notFound', { defaultValue: 'Not Found' })
                }
              />
            ),
          },
        ]}
        rows={rows.map((row) => ({ id: row.id, data: row }))}
      />

      <Box flx="row" jc="fe" gapSize="12px">
        <Button disabled={busy} onClick={onRemove}>
          {t('removeDomainButton', { defaultValue: 'Remove Domain' })}
        </Button>

        {!isVerified && (
          <Button colorScheme="primary" disabled={busy} onClick={onVerify}>
            {t('verifyButton', { defaultValue: 'Verify' })}
          </Button>
        )}
      </Box>
    </Card>
  );
}
